<script setup lang="ts">
import { type Component, computed, defineAsyncComponent } from 'vue'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import {
  DateFieldList,
  FileFieldList,
  InteratorFieldList,
  ListFieldList,
  LogicFieldList,
  NumberFieldList,
  OptionalFieldList,
  ReferenceFieldList,
  TextFieldList,
} from './../model/types.ts'
import InventBoolTypeField from './fields/InventBoolTypeField.vue'
import InventNumberTypeField from './fields/InventNumberTypeField.vue'
import InventDateTypeField from './fields/InventDateTypeField.vue'
import InventListTypeField from './fields/InventListTypeField.vue'
import InventInteratorTypeField from './fields/InventInteratorTypeField.vue'
import InventComputedTypeField from './fields/InventComputedTypeField.vue'
import { WFileLoadField } from '@/widgets/FileLoadField'
import InventTextTypeField from '@/entities/Inventory/TypeField/ui/fields/InventTextTypeField.vue'
import InventAddressTypeField from '@/entities/Inventory/TypeField/ui/fields/InventAddressTypeField.vue'
import ObjectLinkSelect from './fields/ObjectLinkSelect.vue'
import InventObjectLinkTypeField from './fields/InventObjectLinkTypeField.vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    type?: string
    fieldData?: ObjectInventoryField
    showMandatoryError?: boolean
    label?: string
    variant?: string
    density?: string
    hideDetails?: boolean
    readonly?: boolean
    disabled?: boolean
    loading?: boolean
  }>(),
  {
    type: TextFieldList.STRING,
    fieldData: () => ({}) as ObjectInventoryField,
    showMandatoryError: false,
    label: '',
    hideDetails: true,
    readonly: false,
    disabled: false,
    loading: false,
  },
)

const model = defineModel<unknown>()

const fieldMeta = computed(() => props.fieldData ?? ({} as ObjectInventoryField))

const field = computed<Component | undefined>(() => {
  switch (props.type) {
    case TextFieldList.STRING:
    case TextFieldList.PHONE:
    case TextFieldList.EMAIL:
    case TextFieldList.LINK:
      return InventTextTypeField
    case TextFieldList.ADDRESS:
      return InventAddressTypeField
    case LogicFieldList.BOOLEAN:
      return InventBoolTypeField
    case NumberFieldList.NUMBER:
    case NumberFieldList.PERCENT:
      return InventNumberTypeField
    case DateFieldList.DATE:
    case DateFieldList.MONTH:
    case DateFieldList.YEAR:
    case DateFieldList.RANGE:
    case DateFieldList.PERIOD:
      return InventDateTypeField
    case ListFieldList.LIST:
    case ListFieldList.LIST_TYPES:
      return InventListTypeField
    case OptionalFieldList.COMPUTED:
      return InventComputedTypeField
    case FileFieldList.LINK_FILE:
      return WFileLoadField
    case InteratorFieldList.SIMPLE:
      return InventInteratorTypeField
    case ReferenceFieldList.OBJECT_LINK:
    case 'link_object':
      return ObjectLinkSelect
  }
})
</script>

<template>
  <component
    :is="field"
    v-model="model"
    :class="$attrs.class"
    :type="type"
    :field="fieldMeta"
    :field-data="fieldMeta"
    :mandatory="fieldMeta.mandatory"
    :items="fieldMeta.items"
    :multiple="fieldMeta.multiple"
    :object_type="fieldMeta.object_type"
    :min="fieldMeta.min"
    :max="fieldMeta.max"
    :use_min="fieldMeta.use_min"
    :use_max="fieldMeta.use_max"
    :length="fieldMeta.length"
    :precision="fieldMeta.precision"
    :label="label"
    :variant="variant"
    :density="density"
    :hide-details="hideDetails"
    :readonly="readonly"
    :disabled="disabled"
    :loading="loading"
    :show-mandatory-error="showMandatoryError"
  />
</template>
